
import { mapGetters } from 'vuex'
import VueScrollClass from 'vue-scroll-class'
import Multiselect from 'vue-multiselect'
import EventSubscribeModal from '@/components/molecules/modals/EventSubscribe'
import { findPixelByVendor } from '~/utils/pixels/helpers'
import { gtmScript } from '~/utils/pixels/gtm'
import { tiktokEvent } from '~/utils/pixels/tiktok'
import { gtagEvent } from '~/utils/pixels/gtag'
import ModalMixin from '@/mixins/modal'
import dayjs from '~/utils/dayjs'
export default {
  name: 'Event',
  components: {
    Multiselect
  },
  directives: {
    'scroll-class': VueScrollClass
  },
  mixins: [ModalMixin],
  async asyncData({ params, error, app, store }) {
    try {
      const event = await app.$axios.$get(`/api/event/${params.eid}`)
      const now = dayjs()
      const salesFrom = dayjs(event.sales_from)
      const countdown = salesFrom.isBefore(now)
        ? 0
        : salesFrom.diff(now, 'millisecond')
      store.dispatch('event/setEvent', event)
      if (event.is_private) {
        const promocode = await app.$cookiz.get(
          `ts__cookie__private__${event.id}`
        )
        if (promocode) {
          await store.dispatch('event/checkPrivate', {
            event: event.id,
            promocode
          })
        }
      }

      return {
        event,
        countdown,
        initialUnlocked: store.getters['event/isUnlocked'](event.id),
        salesFrom: salesFrom.toDate()
      }
    } catch (e) {
      return error({
        statusCode: 404
      })
    }
  },
  data: () => ({
    dateSelect: null,
    mounted: false,
    mobileImageAspectRatio: '2/1'
  }),
  head() {
    const i18nSeo = this.$nuxtI18nSeo()
    const dateFrom = this.dateFrom
      ? this.$options.filters.dateStr(
          this.$options.filters.dateToStr(this.dateFrom)
        )
      : false
    const dateTo = this.dateTo
      ? this.$options.filters.dateStr(
          this.$options.filters.dateToStr(this.dateTo)
        )
      : false
    const date = dateFrom ? `${dateFrom}${dateTo ? ` - ${dateTo}` : ''}` : ''

    const gtms = this.event.tracking.filter((tracker) => tracker.type === 'GTM')

    return {
      ...i18nSeo,
      title: `${this.event.name}, ${date}`,
      meta: [
        ...i18nSeo.meta,
        {
          hid: 'description',
          property: 'description',
          content: this.event.description
            .replace(/(<([^>]+)>)/gi, '')
            .replace(/\r?\n|\r/g, '')
            .slice(0, 500)
            .concat('...')
        },
        { hid: 'og:type', property: 'og:type', content: 'website' },
        {
          hid: 'og:title',
          property: 'og:title',
          content: `${this.event.name}, ${date}`
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.event.description
            .replace(/(<([^>]+)>)/gi, '')
            .replace(/\r?\n|\r/g, '')
            .slice(0, 500)
            .concat('...')
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.ogImage
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$config.HOST}${this.localePath({
            name: 'events-eid',
            params: { eid: this.event.id }
          })}`
        }
      ],
      __dangerouslyDisableSanitizers: ['script', 'noscript'],
      script: [
        ...gtms.map((gtm) => ({
          hid: `script-${gtm.code}`,
          innerHTML: gtmScript(gtm.code),
          type: 'text/javascript',
          charset: 'utf-8'
        }))
      ]
    }
  },
  computed: {
    ...mapGetters({
      // event: 'event/event',
      isUnlocked: 'event/isUnlocked',
      mobileLayout: 'mobileLayout'
    }),
    salesFromFormatted() {
      return dayjs(this.salesFrom).format('DD.MM.YYYY HH:mm')
    },
    isMobile() {
      return this.mobileLayout.includes(this.$mq)
    },
    showUnlockBlock() {
      return !this.isUnlocked(this.event.id) || !this.initialUnlocked
    },
    mobileImage() {
      return this.isMobile && this.event.mobile_image
    },
    imgSize() {
      if (this.mobileImage) {
        return '1000x500'
      }

      return '403x570'
    },
    ogImage() {
      const { mobile_image, avatar } = this.event
      const size = mobile_image ? '1000x500' : '403x570'
      return `${this.$config.CDN_URL}event/${this.event.id}/${
        mobile_image || avatar
      }/${size}.jpg`
    },
    img() {
      if (this.mobileImage) {
        return `${this.$config.CDN_URL}event/${this.event.id}/${this.event.mobile_image}`
      }

      return this.event.avatar
        ? `${this.$config.CDN_URL}event/${this.event.id}/${this.event.avatar}`
        : ''
    },
    orgLogoUrl() {
      return this.event.org.image
        ? `${this.$config.CDN_URL}org/${this.event.org.id}/${this.event.org.image.filename}/175x175.jpg`
        : require('~/assets/images/org.svg')
    },
    venueLogoUrl() {
      return this.event.location.image
        ? `${this.$config.CDN_URL}venue/${this.event.location.id}/${this.event.location.image.filename}/175x175.jpg`
        : require('~/assets/images/venue.svg')
    },
    dateTo() {
      return !!this.event.date_to &&
        this.event.date_to !== '0000-00-00 00:00:00'
        ? this.event.date_to
        : false
    },
    dateFrom() {
      return this.event.date_from || false
    },
    related() {
      return this.event?.related || []
    },
    dateSelectOptions() {
      return this.related.map((event) => {
        return {
          name: event,
          value: event.id
        }
      })
    },
    showPreSaleSubscriptionForm() {
      const { sales_enabled, is_registration_enabled } = this.event
      return !sales_enabled && is_registration_enabled
    }
  },
  destroyed() {
    this.$store.dispatch('event/setEvent', {})
  },
  async mounted() {
    this.$nextTick(() => {
      this.mounted = true
    })

    this.getMobileImageAspectRatio()

    try {
      const { action_tracking } = await this.$axios.$get(
        `/api/event/${this.event.id}/track_view`,
        {
          params: {
            page: 'landing'
          }
        }
      )

      findPixelByVendor(action_tracking, 'gtag').map((pixel) => {
        return gtagEvent(pixel.code, 'conversion', {
          send_to: `${pixel.code}/${pixel.event_id}`,
          ...pixel.params
        })
      })

      findPixelByVendor(action_tracking, 'facebook').map((pixel) => {
        return this.$facebook.event(
          pixel.code,
          pixel.event_type,
          {},
          pixel.event_id
        )
      })

      findPixelByVendor(action_tracking, 'TikTok').map((pixel) => {
        return tiktokEvent(
          pixel.code,
          pixel.event_type,
          {
            contents: [
              {
                content_id: this.event.id,
                content_name: this.event.name
              }
            ]
          },
          pixel.event_id
        )
      })
    } catch (e) {
      console.log(e)
    }
  },
  methods: {
    getMobileImageAspectRatio() {
      if (this.event.mobile_image) {
        const img = new Image()
        img.src = `${this.$config.CDN_URL}event/${this.event.id}/${this.event.mobile_image}/1000x500.webp`
        img.onload = () => {
          this.mobileImageAspectRatio = `${img.width}/${img.height}`
        }
      }
    },
    showSubscriptionModal() {
      this.$modal.show(
        EventSubscribeModal,
        {
          eventId: this.event.id
        },
        {
          height: 'auto',
          width: this.modalWidthCalc(),
          adaptive: true,
          scrollable: true,
          clickToClose: false
        }
      )
    },
    async countdownCallback() {
      try {
        const event = await this.$axios.$get(`/api/event/${this.event.id}`)
        const now = dayjs()
        const salesFrom = dayjs(event.sales_from)
        const countdown = salesFrom.isBefore(now)
          ? 0
          : salesFrom.diff(now, 'millisecond')
        this.$store.dispatch('event/setEvent', event)

        this.event = { ...event }
        this.countdown = countdown
        this.salesFrom = salesFrom.toDate()
      } catch (e) {}
    }
  }
}
